<template>
  <ReleaseNote
    variant="acknowledge"
    :model-value="dialogOpen"
    :notes="notes"
    :busy="saving"
    @acknowledge="onGotIt"
  />
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { edit_user } from "@/graphql/mutations";
import { parseApiJson } from "@/utils/parseApiJson";
import ReleaseNote from "@/components/ReleaseNote.vue";

const client = generateClient();
const RELEASE_NOTES_ACK_KEY = "portalAcknowledgedReleaseNotes";
const RELEASE_NOTES_FILE = "ReleaseNotes.txt";

export default {
  name: "ViewedReleaseNote",
  components: { ReleaseNote },
  props: {
    blocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:open"],
  data() {
    return {
      saving: false,
      notes: "",
      fetchDone: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.currentUser || null;
    },
    isLoggedIn() {
      return Boolean(this.currentUser);
    },
    isLoginPage() {
      const path = this.$route?.path || "/";
      const name = this.$route?.name;
      return path === "/" || path === "/login" || name === "Login";
    },
    hasViewedReleaseNotes() {
      const viewed = this.currentUser?.viewed_release_notes;
      return viewed === true || viewed === "true";
    },
    dialogOpen() {
      if (this.blocked) return false;
      if (!this.isLoggedIn || this.isLoginPage) return false;
      if (this.currentUser?.is_terms_and_conditions_acknowledged === false) {
        return false;
      }
      if (this.hasViewedReleaseNotes) return false;
      if (!this.fetchDone && !this.notes) return false;
      if (!this.notes) return false;
      return true;
    },
  },
  watch: {
    dialogOpen: {
      immediate: true,
      handler(open) {
        this.$emit("update:open", open);
      },
    },
    isLoggedIn: {
      immediate: true,
      handler(loggedIn) {
        if (loggedIn) this.fetchViewedReleaseNotes();
      },
    },
  },
  mounted() {
    this.fetchViewedReleaseNotes();
  },
  methods: {
    appAssetUrl(fileName) {
      const base = process.env.BASE_URL || "/";
      const normalizedBase = base.endsWith("/") ? base : `${base}/`;
      return `${normalizedBase}${fileName}`;
    },
    isUsableNotesText(text) {
      const trimmed = String(text || "").trim();
      if (!trimmed) return false;
      const lower = trimmed.toLowerCase();
      if (
        lower.startsWith("<!doctype") ||
        lower.startsWith("<html") ||
        lower.startsWith("<?xml") ||
        lower.startsWith("<error")
      ) {
        return false;
      }
      return true;
    },
    async fetchViewedReleaseNotes() {
      try {
        const url = `${this.appAssetUrl(RELEASE_NOTES_FILE)}?t=${Date.now()}`;
        const res = await fetch(url, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (!res.ok) {
          this.notes = "";
          return;
        }
        const trimmed = ((await res.text()) || "").trim();
        this.notes = this.isUsableNotesText(trimmed) ? trimmed : "";
      } catch (error) {
        console.warn("[Release notes] ReleaseNotes.txt fetch failed:", error);
        this.notes = "";
      } finally {
        this.fetchDone = true;
      }
    },
    async onGotIt() {
      if (this.saving) return;

      const currentUser = this.currentUser;
      const updatingUserId = currentUser?.user_id;
      if (!updatingUserId) return;

      this.saving = true;
      try {
        const response = await client.graphql({
          query: edit_user,
          variables: {
            input: {
              updating_user_id: updatingUserId,
              viewed_release_notes: true,
            },
          },
        });

        const responseData = parseApiJson(response.data.edit_user, {});
        if (responseData.status !== "SUCCESS") {
          console.error(
            "[Release notes] Failed to update viewed_release_notes",
            responseData
          );
          return;
        }

        try {
          const notes = String(this.notes || "").trim();
          if (notes) {
            localStorage.setItem(RELEASE_NOTES_ACK_KEY, notes);
          }
        } catch (e) {
          /* ignore */
        }

        this.$store.dispatch("setCurrentUser", {
          ...currentUser,
          viewed_release_notes: true,
        });
      } catch (error) {
        console.error("[Release notes] viewed_release_notes update failed:", error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
